import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useEventCallback, useEventListener } from 'usehooks-ts'

import MenuIcon from '@wwt/shared/assets/icons/outline/menu.svg'

import { SupportButton } from '@/features/SupportButton/ui/SupportButton.tsx'
import { AccountMenu } from '@/features/account'
import { LoginButton, RegisterButton } from '@/features/auth'
import { NotificationsMenu } from '@/features/notifications'
import { ShareMenu } from '@/features/share'
import { Button, type ButtonProps } from '@/shared/ui/Button'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from '@/shared/ui/Dialog'
import {
	NestedMenu,
	NestedMenuBackButton,
	NestedMenuContent,
	NestedMenuTitle
} from '@/shared/ui/NestedMenu'
import { Separator } from '@/shared/ui/Separator'

interface MobileMenuProps extends ButtonProps {
	isAuthorized: boolean
}

export const MobileMenu = ({ isAuthorized, ...props }: MobileMenuProps) => {
	const { t } = useTranslation('header')

	const [open, setOpen] = useState(false)

	const handleViewportResize = useEventCallback(() => {
		if (window.innerWidth >= 1280) {
			setOpen(false)
		}
	})

	useEventListener('resize', handleViewportResize)

	return (
		<Dialog
			open={open}
			onOpenChange={setOpen}
		>
			<DialogTrigger asChild>
				<Button
					variant="tertiary"
					size="icon-sm"
					{...props}
				>
					<MenuIcon />
					<span className="sr-only">{t('mobileMenu.open')}</span>
				</Button>
			</DialogTrigger>
			<DialogContent className="max-sm:pb-20 xl:hidden">
				<NestedMenu>
					<DialogHeader>
						<DialogTitle>
							<NestedMenuTitle
								// TODO: better types
								pathToString={path =>
									path ? t(`menu.${path as 'menu'}`) : t('menu.menu')
								}
							/>
						</DialogTitle>
					</DialogHeader>
					<Separator />
					<NestedMenuContent path="root">
						<ShareMenu />
						<NotificationsMenu />
						<AccountMenu />
					</NestedMenuContent>

					<NestedMenuContent
						className="flex-1 items-end"
						path="root"
					>
						{isAuthorized ? (
							<DialogFooter className="max-sm:px-5 sm:col-span-2">
								<SupportButton />
							</DialogFooter>
						) : (
							<DialogFooter className="row-auto flex-col gap-4 max-sm:px-5 sm:col-span-2 md:gap-6">
								<div className="flex flex-col gap-4 sm:flex-row sm:gap-8">
									<div className="sm:w-1/2">
										<SupportButton />
									</div>
									<LoginButton
										variant="outline"
										className="sm:w-1/2"
									/>
								</div>
								<RegisterButton variant="default" />
							</DialogFooter>
						)}
					</NestedMenuContent>

					<NestedMenuBackButton className="absolute top-3.5 left-2 rounded-full sm:top-10 sm:left-8 xl:top-11" />
				</NestedMenu>
			</DialogContent>
		</Dialog>
	)
}
